// window.global = window;
import * as Sentry from "@sentry/react";
import { polyfillCountryFlagEmojis } from "country-flag-emoji-polyfill";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PersistGate } from "redux-persist/integration/react";
import App from "./App";
import "./i18n/config";
import "./index.scss";
import { initDynamicTooltips } from "./lib/dynamicTooltip";
import { getItemWithExpiry } from "./lib/utility";
import { persistor, store } from "./redux/store";   
import { WebSocketNotificationProvider } from "./contexts/WebSocketNotification";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FullscreenProvider } from "./contexts/FullscreenContext";

// Windows/Chromium: country flags render as letter codes (e.g. "IN") without this.
// Only loads a flag webfont when the browser lacks native flag support; no effect elsewhere.
polyfillCountryFlagEmojis();

try {
  const isDark = localStorage.getItem("darkMode") === "true";
  if (isDark) {
    document.body.classList.add("dark-mode");
  } else {
    document.body.classList.remove("dark-mode");
  }
} catch (e) {}

initDynamicTooltips();


/** Root-absolute or absolute URL so nested routes don't resolve favicon as /smsc/GW1.svg etc. */
const normalizeAssetUrl = (url, fallback = "/logo.png") => {
  const value = String(url || "").trim();
  if (!value) return fallback;
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  return value.startsWith("/") ? value : `/${value}`;
};

const updateFavicon = () => {
  try {
    if (import.meta.env.VITE_REACT_SERVER_CONFIGURATION) {
      const serverconfiguration = JSON.parse(
        import.meta.env.VITE_REACT_SERVER_CONFIGURATION
      );
      if (serverconfiguration?.favicon) {
        const link =
          document.querySelector("link[rel~='icon']") ||
          (() => {
            const created = document.createElement("link");
            created.rel = "icon";
            document.head.appendChild(created);
            return created;
          })();
        link.type = "image/svg+xml";
        link.href = normalizeAssetUrl(serverconfiguration.favicon);
      }
    }
  } catch (error) {
    console.log("Using default favicon from HTML");
  }
};

updateFavicon();

const Fallback = ({ error, resetErrorBoundary }) => (
  <div style={{ backgroundColor: "yellow", padding: "10px" }}>
    <h2 style={{ color: "teal" }}>Something went wrong</h2>
    <p style={{ color: "red" }}>Error message: {error.message}</p>
    <button onClick={resetErrorBoundary}>Try again</button>
  </div>
);

const user = (globalThis.profileData =
  getItemWithExpiry("profileData") || null);

Sentry.init({
  dsn: "https://97ffc138f2a69f54a0624cc4b9cc6086@o4508438638886912.ingest.de.sentry.io/4508442313752656",
  environment: "production",

  beforeSend(event, hint) {
    if (hint?.originalException?.message === "Network Error") {
      return null;
    }

    if (hint?.originalException instanceof TypeError) {
      return null;
    }
    if (hint?.originalException instanceof ReferenceError) {
      return null;
    }

    if (event.environment === "development") {
      return null;
    }

    if (event.level === "warning") {
      return null;
    }

    event.tags = {
      ...event.tags,
      customTag: "CustomTagValue",
    };

    event.extra = {
      ...event.extra,
      debugInfo: "Additional debug context",
    };

    event.user = {
      ...event.user,
      id: user?.masterID || "unknown",
      email: user?.email || "unknown@example.com",
      username: user?.userName || "Guest",
    };

    if (event.tags?.feature === "login") {
      return null;
    }

    return event;
  },
});

createRoot(document.getElementById("root")).render(
  <>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <WebSocketNotificationProvider>
          <ThemeProvider>
            <FullscreenProvider>
              <ToastContainer style={{ zIndex: 100000 }} />
              <Sentry.ErrorBoundary fallback={<Fallback />}>
                <App />
              </Sentry.ErrorBoundary>
            </FullscreenProvider>
          </ThemeProvider>
        </WebSocketNotificationProvider>
      </PersistGate>
    </Provider>
  </>
);
